/* PDP media gallery - desktop vertical scroller, 1024px and up

   <slideshow-container> is a fixed frame with the images stacked inside it and
   the movement is native scroll, not animation. assets/yg-custom.css takes the
   overflow off <slideshow-slides>, so the Scroller assets/slideshow.js binds to
   it never sees a scroll event and never fights the frame; select() still does
   its bookkeeping, but its scroll call lands on an element that cannot scroll.
   Nothing here applies below 1024px. */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 1024px)';
  var FRAME = 'slideshow-container';
  var THUMBNAIL = '.slideshow-controls__thumbnail';
  var ARROW = '.slideshow-control--next, .slideshow-control--previous';
  var INTERACTIVE = 'model-viewer, video, iframe, button, a, input, label, dialog';
  var RATIOS = [0, 0.2, 0.4, 0.6, 0.8, 1];
  /* scrollTop lands on fractional values at fractional DPRs, so === never ends */
  var EDGE = 1;
  /* quiet time marking the end of a wheel gesture, momentum included */
  var GESTURE_GAP = 200;
  /* cap on a named slide's hold - only reached where scrollend is missing
     (Safari below 18.2) or a glide is interrupted by something not a wheel */
  var SETTLE_MAX = 1200;
  /* arrival slack - slide heights land on fractional values at fractional DPRs */
  var ARRIVED = 2;

  var desktop = window.matchMedia(DESKTOP);
  var bound = new WeakSet();
  var observer = new MutationObserver(schedule);
  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      init();
    });
  }

  function init() {
    var galleries = document.querySelectorAll('media-gallery');

    for (var i = 0; i < galleries.length; i++) {
      var gallery = galleries[i];

      /* media-gallery.js swaps the whole <media-gallery> out on a variant
         change - watch the parent to bind whatever replaces it */
      if (gallery.parentElement) observer.observe(gallery.parentElement, { childList: true });

      if (bound.has(gallery)) continue;
      bound.add(gallery);
      attach(gallery);
    }
  }

  /**
   * The slides the theme considers live, in slideshow.current's order.
   *
   * @param {Element} slideshow
   * @param {Element} frame
   * @returns {Element[]}
   */
  function slidesOf(slideshow, frame) {
    var live = slideshow && slideshow.slides;
    if (live && live.length) return live;

    return Array.prototype.filter.call(frame.querySelectorAll('slideshow-slide'), function (slide) {
      return !slide.hasAttribute('hidden') || slide.hasAttribute('reveal');
    });
  }

  /**
   * Scrolls the frame so a slide sits at its top edge. From rects rather than
   * offsetTop, which depends on the nearest positioned ancestor. `behavior` is
   * per call because the frame's CSS deliberately carries no scroll-behavior.
   *
   * @param {Element} container
   * @param {Element} child
   * @param {ScrollBehavior} behavior
   */
  function scrollTo(container, child, behavior) {
    var top = container.scrollTop + child.getBoundingClientRect().top - container.getBoundingClientRect().top;

    container.scrollTo({ top: top, behavior: behavior });
  }

  /**
   * The frame is as tall as one image, so its ratio has to be the product's.
   * Measuring the first slide's box covers the fixed-ratio settings too.
   *
   * @param {Element} frame
   */
  function sizeFrame(frame) {
    var slide = frame.querySelector('slideshow-slide');
    if (!slide) return;

    var box = slide.getBoundingClientRect();
    if (!box.width || !box.height) return;

    /* writing the same value back resizes nothing, but still re-runs the
       ResizeObserver below */
    var ratio = (box.width / box.height).toFixed(4);
    if (frame.style.getPropertyValue('--yg-frame-ratio') === ratio) return;

    frame.style.setProperty('--yg-frame-ratio', ratio);
  }

  /**
   * Slideshow#visibleSlides measures on the x axis, and stacked vertically
   * every slide shares the same x extent - so all of them read as visible and
   * the arrows would jump the length of the gallery. Shadow the getters with a
   * one-slide step, leaving select()'s wrap at the ends intact.
   *
   * @param {Element} slideshow
   * @param {string} name
   * @param {number} step
   */
  function patchStep(slideshow, name, step) {
    var original = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(slideshow), name);
    if (!original || typeof original.get !== 'function') return;

    Object.defineProperty(slideshow, name, {
      configurable: true,
      get: function () {
        if (!desktop.matches) return original.get.call(this);
        return this.current + step;
      },
    });
  }

  /**
   * @param {Element} gallery
   */
  function attach(gallery) {
    var frame = gallery.querySelector(FRAME);
    if (!frame) return;

    var slideshow = frame.closest('slideshow-component');
    var ratios = new Map();
    /* Boundary-release state, per gesture rather than per event. */
    var lastWheel = 0;
    var released = false;
    var reclaimTimer = null;
    /* -1 when nothing is named and the observer owns the highlight. */
    var heldIndex = -1;
    var settleTimer = null;

    /**
     * Held by index, not a bare flag, so the observer cannot hand the highlight
     * back to a slide the shopper never asked for: a glide crosses every slide
     * in between and walked the outline down the strip one thumbnail at a time.
     *
     * @param {number} index
     */
    function holdIndex(index) {
      heldIndex = index;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(releaseIndex, SETTLE_MAX);
    }

    /** Gives the highlight back to the observer. */
    function releaseIndex() {
      window.clearTimeout(settleTimer);
      settleTimer = null;
      heldIndex = -1;
    }

    /**
     * scrollend means the frame stopped, not that it arrived - a glide cut short
     * fires it just the same. Release on arrival or at the end of the range;
     * anything else keeps holding and leaves SETTLE_MAX to end it.
     */
    function onScrollEnd() {
      if (heldIndex < 0) return;

      var max = frame.scrollHeight - frame.clientHeight;
      if (frame.scrollTop >= max - EDGE) return releaseIndex();

      var slide = slidesOf(slideshow, frame)[heldIndex];
      if (!slide) return releaseIndex();

      var off = slide.getBoundingClientRect().top - frame.getBoundingClientRect().top;
      if (Math.abs(off) <= ARRIVED) releaseIndex();
    }

    /**
     * Stops slideshow.js starting a drag, and the browser a native image drag.
     *
     * @param {MouseEvent} event
     */
    function blockDrag(event) {
      if (!desktop.matches) return;

      var target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('slideshow-slides')) return;
      if (target.closest(INTERACTIVE)) return;

      event.preventDefault();
      event.stopPropagation();
    }

    /**
     * The theme has already applied the index - only a frame to move.
     *
     * @param {MouseEvent} event
     */
    function onClick(event) {
      if (!desktop.matches || !slideshow) return;

      var target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest(THUMBNAIL + ', ' + ARROW)) return;

      var slides = slidesOf(slideshow, frame);
      var slide = slides[slideshow.current];
      if (!slide) return;

      holdIndex(slideshow.current);
      scrollTo(frame, slide, 'smooth');
    }

    /**
     * Boundary release at the first and last image.
     *
     * Native overscroll chaining is not enough: Chrome latches a continuous
     * gesture - trackpad scroll, wheel momentum - to whichever scroller it
     * started on, so the rest of it is still swallowed after the frame hits its
     * end. Discrete wheel clicks chain on their own and never reach this.
     * Only the two boundaries are intercepted.
     *
     * @param {WheelEvent} event
     */
    function onWheel(event) {
      if (!desktop.matches) return;

      /* the shopper is driving, so a named slide is no longer the destination */
      releaseIndex();

      var now = event.timeStamp || performance.now();
      /* a gap this long means the gesture ended; the next event starts a fresh
         one and the frame owns it again */
      if (now - lastWheel > GESTURE_GAP) reclaim();
      lastWheel = now;

      window.clearTimeout(reclaimTimer);
      reclaimTimer = window.setTimeout(reclaim, GESTURE_GAP);

      /* Latched: no boundary re-check and no cursor reading, which is what
         stopped a wiggle at the last image flipping the decision back. */
      if (released) return;

      var dy = event.deltaY;
      if (!dy) return;

      var max = frame.scrollHeight - frame.clientHeight;
      var past = dy > 0 ? frame.scrollTop >= max - EDGE : frame.scrollTop <= EDGE;
      if (!past) return;

      release();
    }

    /**
     * Hands the gesture over by taking the frame out of the scroll chain, not by
     * preventing the event and scrolling the page ourselves - that replaced the
     * browser's own wheel smoothing and trackpad momentum with discrete jumps.
     * scrollTop survives overflow:hidden, so the gallery stays where it was.
     */
    function release() {
      if (released) return;
      released = true;
      frame.style.overflowY = 'hidden';
    }

    function reclaim() {
      if (!released) return;
      released = false;
      frame.style.overflowY = '';
    }

    /**
     * Hover-to-navigate: a click gets its index from the theme's on:click
     * delegation, hover has none, so both halves happen here. Delegated from
     * mouseover because mouseenter does not bubble; the repeat firings for each
     * child crossed are absorbed by the index guard.
     *
     * @param {MouseEvent} event
     */
    function onThumbnailHover(event) {
      if (!desktop.matches || !slideshow) return;

      var target = event.target;
      if (!(target instanceof Element)) return;

      var thumb = target.closest(THUMBNAIL);
      if (!thumb || !gallery.contains(thumb)) return;

      var thumbs = gallery.querySelectorAll(THUMBNAIL);
      var index = Array.prototype.indexOf.call(thumbs, thumb);
      if (index < 0 || index === slideshow.current) return;

      var slide = slidesOf(slideshow, frame)[index];
      if (!slide) return;

      holdIndex(index);
      slideshow.current = index;
      scrollTo(frame, slide, 'smooth');
    }

    /**
     * @param {IntersectionObserverEntry[]} entries
     */
    function onIntersect(entries) {
      if (!desktop.matches || !slideshow || typeof slideshow.next !== 'function') return;

      for (var i = 0; i < entries.length; i++) ratios.set(entries[i].target, entries[i].intersectionRatio);

      /* kept current through the hold so the first reading after it is right */
      if (heldIndex >= 0) return;

      var best = null;
      var bestRatio = 0;
      ratios.forEach(function (ratio, slide) {
        /* Insertion order means a tie goes to the slide nearer the top. */
        if (ratio > bestRatio) {
          bestRatio = ratio;
          best = slide;
        }
      });
      if (!best) return;

      var index = slidesOf(slideshow, frame).indexOf(best);
      if (index < 0 || index === slideshow.current) return;

      /* Reporting only - nothing in this path may scroll anything: a programmatic
         scroll landing mid-gesture is what makes a scroller catch. Slideshow's
         `current` setter is bookkeeping and does not move the frame. */
      slideshow.current = index;
    }

    gallery.addEventListener('mousedown', blockDrag, true);
    gallery.addEventListener('click', onClick);
    gallery.addEventListener('mouseover', onThumbnailHover);
    /* passive: never calls preventDefault, and saying so keeps it off the
       browser's critical path for scrolling */
    frame.addEventListener('wheel', onWheel, { passive: true });
    /* glide over; SETTLE_MAX covers the browsers that do not fire this */
    frame.addEventListener('scrollend', onScrollEnd);

    sizeFrame(frame);

    var slides = frame.querySelectorAll('slideshow-slide');
    var watcher = new IntersectionObserver(onIntersect, { root: frame, threshold: RATIOS });
    for (var i = 0; i < slides.length; i++) watcher.observe(slides[i]);

    /* ResizeObserver, not a resize listener: the grid sets the frame's width
       and the theme editor can change it without the window moving */
    new ResizeObserver(function () {
      sizeFrame(frame);
    }).observe(frame);

    if (!slideshow || !window.customElements) return;

    window.customElements.whenDefined('slideshow-component').then(function () {
      patchStep(slideshow, 'nextIndex', 1);
      patchStep(slideshow, 'previousIndex', -1);
      showInitialSlide();

      /* the theme rewrites initial-slide when a variant pins a different image
           and the gallery is morphed rather than replaced */
      new MutationObserver(showInitialSlide).observe(slideshow, {
        attributes: true,
        attributeFilter: ['initial-slide'],
      });
    });

    function showInitialSlide() {
      if (!desktop.matches) return;

      var index = parseInt(slideshow.getAttribute('initial-slide') || '0', 10);
      if (!index) return;

      var slide = slidesOf(slideshow, frame)[index];
      if (slide) scrollTo(frame, slide, 'instant');
    }
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function () {
    observer.disconnect();
    init();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
